import { useEffect, useId, useRef, useState } from 'react'

import { cn } from '../cn'
import { FormField } from './FormField'
import { Input } from './Input'
import { Button } from '../primitives/Button'

// ViewEditCard — the save model for stable, important data (profile, billing,
// brand): read-only until you deliberately enter edit and Save; Cancel
// discards (design/components/vieweditcard.html). NOT auto-save. Concierge
// fields are managed by INHERTA: shown but locked, with a ConciergeLockChip and
// who to write to — the value is never hidden, only its editing.

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

export interface ConciergeLockChipProps {
  /** Account manager to write to (surfaced as a tooltip). */
  managerName?: string | undefined
  className?: string
}

export function ConciergeLockChip({ managerName, className }: ConciergeLockChipProps) {
  return (
    <span
      title={managerName ? `Administrado por INHERTA · ${managerName}` : 'Administrado por INHERTA'}
      className={cn(
        'ml-2 inline-flex items-center gap-[5px] rounded-xs bg-field px-1.5 py-0.5 align-middle text-[9.5px] font-bold tracking-[0.03em] text-text-3',
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-[9px]"
      >
        <rect x="3.5" y="11" width="17" height="10" rx="2.2" />
        <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
      </svg>
      Administra INHERTA
    </span>
  )
}

export interface ViewEditField {
  key: string
  label: string
  value: string
  editable: boolean
  /** If set, the field is concierge-locked; the name is who to write to. */
  lockedBy?: string
  /** Status/help copy shown under the field in BOTH view and edit mode
   *  (e.g. "Pendiente de confirmación…" for an unverified secondary email). */
  helper?: string
}

export interface ViewEditCardProps {
  title: string
  fields: ViewEditField[]
  onSave: (patch: Record<string, string>) => Promise<void>
  /** When false the card stays in view mode with no Editar button. */
  canEdit: boolean
  className?: string
}

export function ViewEditCard({ title, fields, onSave, canEdit, className }: ViewEditCardProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const idBase = useId()

  const firstEditableKey = fields.find((f) => f.editable && !f.lockedBy)?.key

  useEffect(() => {
    if (editing) firstFieldRef.current?.focus()
  }, [editing])

  function startEdit() {
    setDraft(Object.fromEntries(fields.map((f) => [f.key, f.value])))
    setError(null)
    setEditing(true)
  }
  function cancel() {
    if (saving) return
    setEditing(false)
    setError(null)
  }
  async function save() {
    setSaving(true)
    setError(null)
    const patch: Record<string, string> = {}
    for (const f of fields) {
      if (f.editable && !f.lockedBy && draft[f.key] !== f.value) patch[f.key] = draft[f.key] ?? ''
    }
    try {
      await onSave(patch)
      setEditing(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar. Intenta de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={cn('w-full rounded-lg border border-line bg-surface p-[18px]', className)}>
      <div className="mb-[15px] flex items-center gap-2.5">
        <span className="text-[13.5px] font-bold text-text">{title}</span>
        {editing ? (
          <span
            role="status"
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-link"
          >
            <PencilIcon />
            Editando
          </span>
        ) : (
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={startEdit}
              iconStart={<PencilIcon />}
            >
              Editar
            </Button>
          )
        )}
      </div>

      {editing ? (
        <>
          <div className="flex flex-col gap-[13px]">
            {fields
              .filter((f) => f.editable)
              .map((f) => {
                const fieldId = `${idBase}-${f.key}`
                const hintId = `${fieldId}-lock`
                const locked = Boolean(f.lockedBy)
                return (
                  <div key={f.key}>
                    <FormField
                      htmlFor={fieldId}
                      label={f.label}
                      labelSuffix={
                        locked ? <ConciergeLockChip managerName={f.lockedBy} /> : undefined
                      }
                      {...(f.helper !== undefined ? { helper: f.helper } : {})}
                    >
                      <Input
                        ref={f.key === firstEditableKey ? firstFieldRef : undefined}
                        value={draft[f.key] ?? ''}
                        disabled={locked || saving}
                        aria-describedby={locked ? hintId : undefined}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      />
                    </FormField>
                    {locked && (
                      <span
                        id={hintId}
                        className="mt-1.5 flex gap-1.5 text-[10.5px] leading-normal text-text-3"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                          className="mt-px size-3 shrink-0"
                        >
                          <circle cx="12" cy="12" r="9.5" />
                          <path d="M12 11v5M12 7.5h.01" />
                        </svg>
                        Para cambios, escribe a {f.lockedBy} (tu account manager).
                      </span>
                    )}
                  </div>
                )
              })}
          </div>

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-md bg-sens-bg px-3 py-2 text-[11px] font-medium text-sens"
            >
              {error}
            </p>
          )}

          <div className="mt-4 flex items-center gap-2.5 border-t border-line pt-3.5">
            <Button size="sm" className="ml-auto" loading={saving} onClick={() => void save()}>
              Guardar
            </Button>
            <Button variant="ghost" size="sm" disabled={saving} onClick={cancel}>
              Cancelar
            </Button>
          </div>
        </>
      ) : (
        <dl className="flex flex-col gap-[13px]">
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <div className="flex items-baseline gap-3.5">
                <dt className="w-[124px] flex-none text-[11.5px] text-text-3">
                  {f.label}
                  {f.lockedBy && <ConciergeLockChip managerName={f.lockedBy} />}
                </dt>
                <dd className="text-[12.5px] font-medium text-text">{f.value}</dd>
              </div>
              {f.helper && <p className="ml-[139.5px] text-[10.5px] text-text-3">{f.helper}</p>}
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
